<template>
  <div class="relative inline-block">
    <!-- The element that triggers the tooltip -->
    <div
      ref="referenceElement"
      class="w-fit"
      @mouseenter="open = true"
      @mouseleave="open = false"
    >
      <slot name="reference" />
    </div>

    <!-- The tooltip itself -->
    <div
      v-if="open"
      ref="floatingElement"
      class="tooltip-body"
      :style="floatingStyles"
    >
      <slot />

      <!-- The arrow element now uses dynamic classes for its borders -->
      <div
        ref="arrowElement"
        :class="arrowClasses"
        :style="arrowStyle"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import {
  useFloating,
  offset,
  flip,
  shift,
  arrow,
  autoUpdate,
} from '@floating-ui/vue';

const open = ref(false);

const referenceElement = ref(null);
const floatingElement = ref(null);
const arrowElement = ref(null);

const { floatingStyles, middlewareData, placement } = useFloating(
  referenceElement,
  floatingElement,
  {
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(15),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowElement }),
    ],
  },
);

// Dynamic positioning for the arrow element
const arrowStyle = computed(() => {
  const { x, y } = middlewareData.value.arrow || {};

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.value.split('-')[0]];

  return {
    left: x != null ? `${x}px` : '',
    top: y != null ? `${y}px` : '',
    [staticSide]: '-10px',
  };
});

// Dynamic styling for the arrow's borders
const arrowClasses = computed(() => {
  // Get the primary placement (top, bottom, left, right)
  const side = placement.value.split('-')[0];

  const staticClass = 'tooltip-arrow';
  let dynamicClass = '';

  switch (side) {
    case 'top':
      dynamicClass = 'arrow-pointing-down';
      break;
    case 'left':
      dynamicClass = 'arrow-pointing-right';
      break;
    case 'right':
      dynamicClass = 'arrow-pointing-left';
      break;
    default:
      dynamicClass = 'arrow-pointing-up';
      break;
  }

  return [staticClass, dynamicClass];
});
</script>

<style scoped>
.tooltip-body {
  position: absolute;
  z-index: 50;
  padding: 8px 12px;
  background-color: #eff6ff;
  border: 1px solid #dbeafe;
  color: #1f2937;
  border-radius: 6px;
  font-size: 0.875rem;
  line-height: 1.25rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);

  width: max-content;
}

/* Base styles for the arrow (size, z-index, etc.) */
/* The borders have been REMOVED from this base class. */
.tooltip-arrow {
  position: absolute;
  z-index: -1;
  width: 20px;
  height: 20px;
  background: inherit;
  transform: rotate(45deg);
}

/* --- Dynamic Border Classes --- */

/* For when the arrow is on TOP of the tooltip (pointing up) */
.arrow-pointing-up {
  border-top: 1px solid #dbeafe;
  border-left: 1px solid #dbeafe;
}

/* For when the arrow is at the BOTTOM of the tooltip (pointing down) */
.arrow-pointing-down {
  border-bottom: 1px solid #dbeafe;
  border-right: 1px solid #dbeafe;
}

/* For when the arrow is on the LEFT of the tooltip (pointing left) */
.arrow-pointing-left {
  border-bottom: 1px solid #dbeafe;
  border-left: 1px solid #dbeafe;
}

/* For when the arrow is on the RIGHT of the tooltip (pointing right) */
.arrow-pointing-right {
  border-top: 1px solid #dbeafe;
  border-right: 1px solid #dbeafe;
}

 .users-list-tooltip {
   .tooltip-body {
     @apply max-w-[500px]
   }
 }

.document-tooltip {
  .tooltip-body {
    @apply max-w-[300px]
  }
}
</style>
